import React, {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState
} from "react";
import { Route, Routes, useLocation, Link } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import Spinner from "@/components/ui/spinner";
import Sidebar from "@/Sidebar";
import People from "../pages/People";
import Companies from "../pages/companies/Companies";
import Partners from "../pages/partners/Partners";
import TasksPage from "@/pages/tasks/TasksPage";
import Deals from "../pages/deals/Deals";
import Contracts from "../pages/contracts/Contracts";
import Services from "../pages/Services";
import Upsells from "../pages/Upsells";
import useAccount from "@/entities/accounts/hooks";
import { useMyRoleType, useUsersMe } from "@/entities/users/hooks";
import { getPageNameFromPath } from "@/utilities";
import { SETTINGS_REFERRER_KEY } from "@/constants";
import { useAnalytics } from "@/services/analytics/hook";
import { useFeatureFlags, useFeatureFlag } from "@/services/features/hook";
import { FeatureFlags } from "@/services/features/types";
import { useIsDemoMode } from "@/assets/mockupData";
import { DreamerState } from "@/components/controls/DreamerButton/DreamerButton";
import { Drawer as ChatDrawer } from "@/components/ui/drawer";
import Chat from "@/components/layouts/Chat/Chat";
import { useDreamerDraftDeepLink } from "@/components/layouts/Chat/useDreamerDraftDeepLink";
import { cn } from "@/utils/utils";
import { useTranslation } from "react-i18next";
import {
  Logout,
  Settings,
  Search,
  Connections,
  NoteTaker
} from "@dreamhub/designsystem/icons";
import UserAvatar from "@/components/controls/UserAvatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";
import { useAuth } from "@/services/auth/AuthContext";
import Text from "@/components/ui/text";
import AuthRedirect, { AuthRedirectProps } from "@/services/auth/AuthRedirect";
import DealDetailedView from "@/pages/deals/components/DealDetailedView";
import DuplicateContractPage from "@/pages/contracts/components/duplicate/DuplicateContractPage";
import UpdateTermsPage from "@/pages/contracts/components/updateTerms/UpdateTermsPage";
import { VersionTermsPage } from "@/pages/contracts/components/termsHistory/VersionTermsPage";
import { ProposalEditorPage } from "@/pages/contracts/components/proposals/editor/ProposalEditorPage";
import { ProposalVersionPage } from "@/pages/contracts/components/proposals/editor/ProposalVersionPage";
import ContractDetailedView from "@/pages/contracts/ContractDetailedView/ContractDetailedView";
import Toaster from "@/components/ui/toast";
import InviteNotetakerModal from "@/components/controls/InviteNotetakerModal";
import PersonDetailedView from "./layouts/PersonDetailedView";
import PartnerContactDetailedView from "./layouts/PartnerContactDetailedView";
import CompanyDetailedView from "@/pages/companies/components/CompanyDetailedView/CompanyDetailedView";
import PartnerDetailedView from "@/pages/partners/components/PartnerDetailedView/PartnerDetailedView";
import { useEnablePartnerships } from "@/pages/partners/hooks";
import { ChatMessage } from "@/entities/messaging/types";
import QueuedDreamerButton from "@/components/controls/DreamerButton/QueuedDreamerButton";
import Integrations from "@/pages/Integrations/Integrations";
import FeedbackButton from "@/components/controls/FeedbackButton";
import StoreInitializer from "@/services/store/Initializer";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from "@/components/ui/tooltip";
import PastContracts from "@/pages/companies/components/CompanyCommercials/PastContracts";
import ReservedIdRouteGuard from "@/components/layouts/ReservedIdRouteGuard";
import { useSidebarState } from "@/services/context/SidebarContext";
import { VIEW_PREFIX } from "@/entities/dataTables/helpers";
import TaskPage from "@/components/layouts/TaskPage";
import History from "@/components/layouts/History/History";
import { UserRoles } from "@/services/features/types";
import { IconButton } from "@/components/ui/icon-button";
import HeroHeader from "@/components/layouts/HeroHeader/HeroHeader";
import NewHome from "@/pages/Home/Home";
import ReconnectionBanner from "@/components/controls/Home/ReconnectionBanner";
import { reconnectionBannerLabels } from "@/components/reconnectionBannerLabels";
import ConnectEmailBanner from "@/components/controls/Home/ConnectEmailBanner";
import { useSettingsTokens } from "@/entities/settings/hooks";
import { GlobalSearch } from "@/components/GlobalSearch/GlobalSearch";
import { useGlobalSearchModal } from "@/components/GlobalSearch/hooks/useGlobalSearchModal";
import DownloadPage from "@/pages/DownloadPage/DownloadPage";
import IconContainer from "@/components/ui/icon-container";
import { Service } from "@/pages/settings/components/connectedAccounts/types";
import {
  belongsToService,
  findInvalidServiceToken
} from "@/pages/settings/components/connectedAccounts/serviceConnection";
import { useCanAccessAdminSettings } from "@/pages/settings/hooks";
import { showZoomReconnectBanner } from "@/components/zoomReconnectBanner";
import Leads from "@/pages/leads/Leads";
import LeadDetailedView from "@/pages/leads/components/LeadDetailedView/LeadDetailedView";
import { useRefreshTasksOnDreamerTaskChange } from "@/entities/tasks/useRefreshTasksOnDreamerTaskChange";
import { useBrowserPushNotifications } from "@/hooks/useBrowserPushNotifications";
import { useReportRunNotifications } from "@/components/global/Chat/components/dreamerReports/useReportRunNotifications";
import {
  SandboxBanner,
  SANDBOX_BANNER_HEIGHT_PX
} from "@/components/layouts/SandboxBanner";
import { AccountSwitchMenuItem } from "@/components/controls/AccountSwitchMenuItem";
import { useIsSandboxAccount } from "@/services/auth/useIsSandboxAccount";
import { useIsNoteTakerAvailable } from "@/services/auth/useIsNoteTakerAvailable";

interface DreamerSectionProps {
  isHomePage: boolean;
  isSidebarOpen: boolean;
  animatedHighlight: boolean;
  chatDrawerOpen: boolean;
  setChatDrawerOpen: React.Dispatch<React.SetStateAction<boolean>>;
  notificationMessages: ChatMessage[];
  userData: any;
  messageFromHeader: string;
  setMessageFromHeader: React.Dispatch<React.SetStateAction<string>>;
  handleDreamerNotification: (messages: ChatMessage[]) => void;
  onOpenReportRunNotice: (message: ChatMessage) => void;
  focusDraftId: string | null;
  onFocusDraftHandled: () => void;
  t: (key: string) => string;
}

export const getDreamerState = (
  chatDrawerOpen: boolean,
  notificationMessages: ChatMessage[]
) =>
  chatDrawerOpen
    ? DreamerState.Collapsed
    : notificationMessages.length !== 0
      ? DreamerState.Notification
      : DreamerState.Normal;

const DreamerSection: React.FC<DreamerSectionProps> = ({
  isHomePage,
  isSidebarOpen,
  animatedHighlight,
  chatDrawerOpen,
  setChatDrawerOpen,
  notificationMessages,
  userData,
  messageFromHeader,
  setMessageFromHeader,
  handleDreamerNotification,
  onOpenReportRunNotice,
  focusDraftId,
  onFocusDraftHandled,
  t
}) => {
  const openChatOrNotice = () => {
    // The button shows the newest notice; a finished report run's opens that report.
    const newestNotice = notificationMessages[notificationMessages.length - 1];
    if (!chatDrawerOpen && newestNotice?.reportRunId) {
      onOpenReportRunNotice(newestNotice);
      return;
    }
    setChatDrawerOpen(!chatDrawerOpen);
  };

  return (
    <div
      className={cn(
        "fixed z-20 flex flex-row w-full left-0 mx-auto justify-center align-center grow-x pointer-events-none",
        isSidebarOpen ? "pl-[208px]" : "pl-[52px]"
      )}
      id="dreamer"
      data-test="dreamer"
    >
      <div className="relative top-[-32px] h-[65px] w-[600px] pointer-events-none">
        <div
          data-test="dreamer-chat-button"
          className={cn(
            "z-10 absolute top-1/2 left-1/2 h-[45px]",
            "transform -translate-y-1/2 -translate-x-[80px] w-[130px]",
            "pointer-events-auto cursor-pointer"
          )}
          onClick={openChatOrNotice}
        />
        <QueuedDreamerButton
          title={t("dreamer")}
          animatedHighlight={isHomePage ? animatedHighlight : false}
          state={getDreamerState(chatDrawerOpen, notificationMessages)}
          notifications={notificationMessages}
        />
      </div>
      <ChatDrawer
        disablePreventScroll={true}
        noBodyStyles={true}
        fixed
        direction="right"
        open={chatDrawerOpen}
        modal={false}
        data-test="chat-drawer"
        dismissible={false}
      >
        <Chat
          selectedUser={userData!}
          messageFromHeader={messageFromHeader}
          setMessageFromHeader={setMessageFromHeader}
          setChatDrawerOpen={setChatDrawerOpen}
          chatDrawerOpen={chatDrawerOpen}
          handleNotification={handleDreamerNotification}
          focusDraftId={focusDraftId}
          onFocusDraftHandled={onFocusDraftHandled}
        />
      </ChatDrawer>
    </div>
  );
};

const MainNavigationAndAuth: React.FC = () => {
  const { t } = useTranslation();
  const { logout, tenantId } = useAuth();
  const isSandbox = useIsSandboxAccount();

  const queryClient = useQueryClient();

  const location = useLocation();
  const [currentPageName, setCurrentPageName] = useState(getPageNameFromPath());
  const contentRef = useRef<HTMLDivElement>(null);
  const [chatDrawerOpen, setChatDrawerOpen] = useState(false);
  const [focusDraftId, clearFocusDraftId] =
    useDreamerDraftDeepLink(setChatDrawerOpen);
  const [notificationMessages, setNotificationMessages] = useState<
    ChatMessage[]
  >([]);
  const [messageFromHeader, setMessageFromHeader] = useState("");
  const [animatedHighlight, setAnimatedHighlight] = useState(true);
  const [authRedirectProps, setShowAuthRedirect] =
    useState<AuthRedirectProps | null>(null);
  const [isInviteNotetakerOpen, setIsInviteNotetakerOpen] = useState(false);
  const inviteNotetakerOpenTimeoutRef = useRef<ReturnType<
    typeof setTimeout
  > | null>(null);
  useEffect(
    () => () => {
      if (inviteNotetakerOpenTimeoutRef.current) {
        clearTimeout(inviteNotetakerOpenTimeoutRef.current);
      }
    },
    []
  );
  const [bannerHeight, setBannerHeight] = useState(0);
  const [isGoogleBannerDismissed, setIsGoogleBannerDismissed] = useState(false);
  const [isMicrosoftBannerDismissed, setIsMicrosoftBannerDismissed] =
    useState(false);
  const [isZoomBannerDismissed, setIsZoomBannerDismissed] = useState(false);
  const { isSidebarOpen } = useSidebarState();
  const {
    isOpen: isSearchOpen,
    openModal: openSearch,
    closeModal: closeSearch
  } = useGlobalSearchModal();
  const {
    isLoading: isTokensLoading,
    isSuccess,
    data: tokensData
  } = useSettingsTokens();

  const shouldShowReconnectionBanner = (
    serviceId: Service,
    isBannerDismissed: boolean
  ): boolean => {
    if (isBannerDismissed || isTokensLoading || !isSuccess || !tokensData) {
      return false;
    }
    if (tokensData?.allValid) {
      return false;
    }
    return !!findInvalidServiceToken(tokensData.tokens, serviceId);
  };

  const { enabled: showPartnerships } = useFeatureFlag(
    FeatureFlags.CRM_7805_PARTNERSHIPS
  );
  const { enabled: showPartnersTable } = useFeatureFlag(
    FeatureFlags.CRM_7917_PARTNERS_TABLE
  );
  const { enabled: showPartnerContacts } = useFeatureFlag(
    FeatureFlags.CRM_8228_PARTNER_CONTACTS_TABLE
  );
  const enablePartnerships = useEnablePartnerships();
  const { enabled: inviteNotetakerEnabled } = useFeatureFlag(
    FeatureFlags.CRM_7174_INVITE_NOTETAKER
  );
  const isNoteTakerAvailable = useIsNoteTakerAvailable();
  const showInviteNotetaker = inviteNotetakerEnabled && isNoteTakerAvailable;
  const { enabled: showDuplicateContract } = useFeatureFlag(
    FeatureFlags.CRM_7713_DUPLICATE_CONTRACT
  );
  const { enabled: showContractAmendments } = useFeatureFlag(
    FeatureFlags.CRM_9208_CONTRACT_AMENDMENTS
  );
  const { enabled: showCpq, loading: cpqLoading } = useFeatureFlag(
    FeatureFlags.CRM_9761_CPQ
  );
  // While the flag loads, a proposal link opened cold shows this, not an empty page.
  const cpqSpinner = (
    <div className="flex h-full items-center justify-center">
      <Spinner size="md" />
    </div>
  );
  const { enabled: showMyTasksPage } = useFeatureFlag(
    FeatureFlags.CRM_9035_MY_TASKS_PAGE
  );

  const hasAnyEmailCalendarToken = useMemo(() => {
    if (isTokensLoading || !isSuccess || !tokensData) {
      return true;
    }

    return (
      tokensData.tokens?.some(
        (token) =>
          belongsToService(token.service, Service.GOOGLE) ||
          belongsToService(token.service, Service.MICROSOFT)
      ) ?? false
    );
  }, [tokensData, isTokensLoading, isSuccess]);

  const isDemoMode = useIsDemoMode();
  // A sandbox can't connect email or calendar, so neither prompt applies there.
  const showConnectEmailBanner =
    !isDemoMode && !isSandbox && !hasAnyEmailCalendarToken;

  const showReconnectToGoogleBanner = useMemo(
    () =>
      !isSandbox &&
      !showConnectEmailBanner &&
      shouldShowReconnectionBanner(Service.GOOGLE, isGoogleBannerDismissed),
    [
      tokensData,
      isTokensLoading,
      isSuccess,
      isGoogleBannerDismissed,
      showConnectEmailBanner,
      isSandbox
    ]
  );

  const showReconnectToMicrosoftBanner = useMemo(
    () =>
      !isSandbox &&
      !showConnectEmailBanner &&
      shouldShowReconnectionBanner(
        Service.MICROSOFT,
        isMicrosoftBannerDismissed
      ),
    [
      tokensData,
      isTokensLoading,
      isSuccess,
      isMicrosoftBannerDismissed,
      showConnectEmailBanner,
      isSandbox
    ]
  );

  const { enabled: zoomReconnectBannerEnabled } = useFeatureFlag(
    FeatureFlags.CRM_11054_ZOOM_RECONNECT_BANNER
  );
  const { enabled: showIntegrationsSettings } = useFeatureFlag(
    FeatureFlags.CRM_9459_INTEGRATIONS_SETTINGS
  );
  const canAccessAdminSettings = useCanAccessAdminSettings();
  const showReconnectToZoomBanner = showZoomReconnectBanner({
    enabled: zoomReconnectBannerEnabled,
    isAdmin: canAccessAdminSettings,
    isSandbox,
    reconnectPending: shouldShowReconnectionBanner(
      Service.ZOOM,
      isZoomBannerDismissed
    )
  });
  const zoomReconnectReason = findInvalidServiceToken(
    tokensData?.tokens,
    Service.ZOOM
  )?.reason;

  const CONNECTION_BANNER_ROW_PX = 36;
  const connectionBannerHeight =
    (isSandbox ? SANDBOX_BANNER_HEIGHT_PX : 0) +
    (showConnectEmailBanner ? CONNECTION_BANNER_ROW_PX : 0) +
    (showReconnectToGoogleBanner ? CONNECTION_BANNER_ROW_PX : 0) +
    (showReconnectToMicrosoftBanner ? CONNECTION_BANNER_ROW_PX : 0) +
    (showReconnectToZoomBanner ? CONNECTION_BANNER_ROW_PX : 0);

  const handleCloseGoogleBanner = () => {
    setIsGoogleBannerDismissed(true);
  };

  const handleCloseMicrosoftBanner = () => {
    setIsMicrosoftBannerDismissed(true);
  };

  const handleCloseZoomBanner = () => {
    setIsZoomBannerDismissed(true);
  };

  const handleGoogleRegister = () => {
    setShowAuthRedirect({
      to: "registerGoogleEmail",
      method: "POST",
      body: {
        services: ["mail", "calendar", "meet"]
      }
    });
  };

  const handleMicrosoftRegister = () => {
    setShowAuthRedirect({
      to: "registerMicrosoftEmail",
      method: "POST",
      body: {
        services: ["mail", "calendar", "meet"]
      }
    });
  };

  const handleZoomRegister = () => {
    setShowAuthRedirect({
      to: "registerZoom",
      method: "POST",
      // Without the Integrations settings pages, Zoom lives in Connected Accounts.
      redirectTo: showIntegrationsSettings
        ? "/settings/integrations/zoom"
        : "/settings/connectedAccounts"
    });
  };

  const handleSettingsNavigation = () => {
    const currentPath = location.pathname + location.search;
    sessionStorage.setItem(SETTINGS_REFERRER_KEY, currentPath);

    queryClient.invalidateQueries({
      queryKey: ["accounts"]
    });
  };

  const handleAuthRedirectDone = () => {
    setShowAuthRedirect(null);
  };

  useEffect(() => {
    setCurrentPageName(getPageNameFromPath());
  }, [location]);

  const accountData = useAccount().data;
  const userData = useUsersMe().data;
  const analytics = useAnalytics();
  const featureFlags = useFeatureFlags();

  const { fireNotification } = useBrowserPushNotifications({
    userId: userData?.id,
    enabled: true
  });

  const isHomePage = currentPageName === "/";
  const { roleType } = useMyRoleType();
  const isAE = roleType === UserRoles.ACCOUNT_EXECUTIVE;

  useEffect(() => {
    if (accountData && userData) {
      analytics.identify(userData, accountData);
      featureFlags.identify(userData, accountData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountData?.id, userData?.id]);

  useEffect(() => {
    if (messageFromHeader.trim() !== "") {
      setChatDrawerOpen(true);
      // Commented-out until we have a UX solution
      // setHeaderExpanded(false);
    }
  }, [messageFromHeader]);

  const refreshTasksOnDreamerTaskChange = useRefreshTasksOnDreamerTaskChange();

  const handleDreamerNotification = (messages: ChatMessage[]) => {
    if (!chatDrawerOpen) {
      // Disable this condition to always show notifications
      // Report-run notices add up, so a second finished run keeps the first,
      // and a Dreamer reply replaces only the notices that are not runs.
      setNotificationMessages((current) =>
        messages.every((message) => message.reportRunId)
          ? [...current, ...messages]
          : [...current.filter((message) => message.reportRunId), ...messages]
      );
    }
    // Refresh task lists when Dreamer creates or completes a task, so they reflect it at once.
    refreshTasksOnDreamerTaskChange(messages);
    if (!chatDrawerOpen) {
      messages.forEach((message) =>
        fireNotification(
          message,
          message.reportRunId ? () => openReportRunNotice(message) : undefined
        )
      );
    }
  };

  const { openReportRunNotice } = useReportRunNotifications({
    chatDrawerOpen,
    setChatDrawerOpen,
    notify: handleDreamerNotification
  });

  const sendQuestionDirectly = (questionText: string) => {
    if (!userData || !questionText.trim()) return;
    setMessageFromHeader(questionText.trim());
  };

  useEffect(() => {
    if (chatDrawerOpen) {
      setNotificationMessages([]);
    }
  }, [chatDrawerOpen]);

  // A finished run's notice names the previous account's report, so an
  // account switch drops it. It runs before paint, like the runs reset, so a
  // run that finishes after the switch cannot be dropped with them.
  const noticesTenantIdRef = useRef(tenantId);
  useLayoutEffect(() => {
    if (noticesTenantIdRef.current === tenantId) return;
    noticesTenantIdRef.current = tenantId;
    setNotificationMessages((current) =>
      current.filter((message) => !message.reportRunId)
    );
  }, [tenantId]);

  useEffect(() => {
    const handleScroll = () => {
      setAnimatedHighlight(window.scrollY <= 0);
    };

    window.addEventListener("scroll", handleScroll);

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  useEffect(() => {
    // Observer for the banner element
    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setBannerHeight(entry.contentRect.height);
      }
    });

    const bannerElement = document.getElementById("pendo-banner");
    if (bannerElement) {
      observer.observe(bannerElement);
    }

    return () => {
      observer.disconnect();
    };
  }, []);

  useEffect(() => {
    const handleFocus = () => {
      queryClient.invalidateQueries({
        queryKey: ["settings/tokens/all"]
      });
    };

    window.addEventListener("focus", handleFocus);
    return () => window.removeEventListener("focus", handleFocus);
  }, [queryClient]);

  return (
    <>
      {/* Put on hold until we improve it, see CRM-1936 */}
      <StoreInitializer />
      {userData && accountData && (
        <>
          <div
            id="pendo-banner"
            className="h-0 w-full bg-surface-elevation1 transition-all duration-300"
          ></div>
          <SandboxBanner />
          {showConnectEmailBanner && (
            <ConnectEmailBanner
              labels={{
                message: t("connectBanner.message"),
                button: t("connectBanner.button")
              }}
            />
          )}
          {showReconnectToGoogleBanner && (
            <ReconnectionBanner
              labels={reconnectionBannerLabels(t, "google")}
              onClose={handleCloseGoogleBanner}
              onReconnect={handleGoogleRegister}
            />
          )}
          {showReconnectToMicrosoftBanner && (
            <ReconnectionBanner
              labels={reconnectionBannerLabels(t, "microsoft")}
              onClose={handleCloseMicrosoftBanner}
              onReconnect={handleMicrosoftRegister}
            />
          )}
          {showReconnectToZoomBanner && (
            <ReconnectionBanner
              labels={reconnectionBannerLabels(t, "zoom", zoomReconnectReason)}
              onClose={handleCloseZoomBanner}
              onReconnect={handleZoomRegister}
            />
          )}
          <div
            className="flex flex-row h-max"
            style={
              {
                "--app-banners-h": `${bannerHeight + connectionBannerHeight}px`
              } as React.CSSProperties
            }
          >
            <div
              id="sticky-bar"
              style={{ top: `${bannerHeight + connectionBannerHeight}px` }}
              className={cn(
                "fixed p-2 right-0 z-50 flex flex-row justify-end items-center max-w-[1700px] pointer-events-none",
                isSidebarOpen ? "w-[calc(100%-208px)]" : "w-[calc(100%-52px)]"
              )}
            >
              <div className="pointer-events-auto">
                <DreamerSection
                  isHomePage={isHomePage}
                  isSidebarOpen={isSidebarOpen}
                  animatedHighlight={animatedHighlight}
                  chatDrawerOpen={chatDrawerOpen}
                  setChatDrawerOpen={setChatDrawerOpen}
                  notificationMessages={notificationMessages}
                  userData={userData}
                  messageFromHeader={messageFromHeader}
                  setMessageFromHeader={setMessageFromHeader}
                  handleDreamerNotification={handleDreamerNotification}
                  onOpenReportRunNotice={openReportRunNotice}
                  focusDraftId={focusDraftId}
                  onFocusDraftHandled={clearFocusDraftId}
                  t={t}
                />
              </div>
              <div className="pointer-events-auto">
                <IconButton
                  onClick={openSearch}
                  variant="iconNoBg"
                  data-test="global-search"
                  id="global-search"
                >
                  <Search />
                </IconButton>
              </div>
              <div className="flex items-center space-x-0.5 gap-4 z-10 pointer-events-auto">
                <div className="flex items-center">
                  <FeedbackButton tooltip={t("feedback")} idContext="home" />

                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Link
                        to={
                          canAccessAdminSettings
                            ? "/settings/general"
                            : isSandbox
                              ? "/settings/apiKeys"
                              : "/settings/connectedAccounts"
                        }
                        onClick={handleSettingsNavigation}
                      >
                        <IconButton variant="iconNoBg">
                          <Settings />
                        </IconButton>
                      </Link>
                    </TooltipTrigger>
                    <TooltipContent>
                      {t("settings.settingsTooltip")}
                    </TooltipContent>
                  </Tooltip>
                </div>
                <DropdownMenu>
                  <DropdownMenuTrigger>
                    <div className="flex gap-2 items-center h-9 rounded-md hover:bg-surface-hover-strong px-2">
                      <Text variant="bodySecondary">
                        {accountData ? accountData.name : t("unknown")}
                      </Text>
                      <UserAvatar dataTest="user-avatar" userData={userData} />
                    </div>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent>
                    <DropdownMenuLabel>
                      <div className="flex flex-col items-start justify-center px-1">
                        <Text as="span" variant="lgLabel">
                          {userData?.displayName}
                        </Text>
                        <Text as="span" variant="body">
                          {userData?.email}
                        </Text>
                        <Text as="span" variant="bodySecondary">
                          {accountData?.name}
                        </Text>
                      </div>
                    </DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    {!isSandbox && (
                      <Link to="/settings/connectedAccounts">
                        <DropdownMenuItem data-test="connected-accounts">
                          <div className="flex flex-row items-center justify-start px-1">
                            <IconContainer size="5" color="secondary">
                              <Connections />
                            </IconContainer>
                            <DropdownMenuLabel>
                              {t("connectedAccounts.title")}
                            </DropdownMenuLabel>
                          </div>
                        </DropdownMenuItem>
                      </Link>
                    )}
                    {showInviteNotetaker && (
                      <DropdownMenuItem
                        data-test="invite-notetaker"
                        onSelect={() => {
                          // Opening the dialog in the same tick the menu closes
                          // overlaps two Radix dismiss layers; the dialog's
                          // scroll-lock cleanup then leaves `pointer-events: none`
                          // stuck on <body>, freezing the app after Cancel.
                          // Defer until the menu's dismiss layer has torn down.
                          inviteNotetakerOpenTimeoutRef.current = setTimeout(
                            () => setIsInviteNotetakerOpen(true),
                            0
                          );
                        }}
                      >
                        <div className="flex flex-row items-center justify-start px-1">
                          <IconContainer size="5" color="secondary">
                            <NoteTaker />
                          </IconContainer>
                          <DropdownMenuLabel>
                            {t("notetaker.inviteMenuItem")}
                          </DropdownMenuLabel>
                        </div>
                      </DropdownMenuItem>
                    )}
                    <AccountSwitchMenuItem />
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      onSelect={() => {
                        // Clear session storage views
                        Object.keys(sessionStorage).forEach((key) => {
                          if (key.startsWith(VIEW_PREFIX)) {
                            sessionStorage.removeItem(key);
                          }
                        });
                        logout();
                      }}
                      data-test="logout"
                    >
                      <div className="flex flex-row items-center justify-start px-1">
                        <IconContainer color="secondary" size="5">
                          <Logout />
                        </IconContainer>
                        <DropdownMenuLabel>
                          {t("avatar.logout")}
                        </DropdownMenuLabel>
                      </div>
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
                {isInviteNotetakerOpen && (
                  <InviteNotetakerModal
                    isOpen={isInviteNotetakerOpen}
                    onClose={() => setIsInviteNotetakerOpen(false)}
                    onRetry={() => setIsInviteNotetakerOpen(true)}
                  />
                )}
              </div>
            </div>
            <Sidebar
              currentPageName={currentPageName}
              onPageClick={(targetName: string) =>
                setCurrentPageName(targetName)
              }
            />
            <div
              className="flex-1 flex flex-col overflow-y-auto ml-0 mr-2 mt-14 mb-6 rounded-[20px] border-[0.5px] border-border-border1"
              style={
                {
                  "--app-content-h": `calc(100vh - 64px - ${connectionBannerHeight}px)`,
                  maxHeight: "var(--app-content-h)",
                  ...(isHomePage ? { scrollbarWidth: "none" } : {})
                } as React.CSSProperties
              }
            >
              <main
                className="flex-1 p-0 bg-surface-elevation0"
                ref={contentRef}
              >
                {isHomePage && (
                  <HeroHeader
                    userData={userData}
                    setMessageFromHeader={setMessageFromHeader}
                    sendQuestionDirectly={sendQuestionDirectly}
                    externalState={getDreamerState(
                      chatDrawerOpen,
                      notificationMessages
                    )}
                    setChatDrawerOpen={setChatDrawerOpen}
                  />
                )}
                <Routes>
                  <Route
                    path=""
                    element={
                      <NewHome
                        userData={userData}
                        isAE={isAE}
                        // onStickyChange={setIsTabsSticky}
                      />
                    }
                  />
                  {showMyTasksPage ? (
                    <Route path="tasks" element={<TasksPage />} />
                  ) : null}
                  <Route
                    path="companies"
                    element={<Companies userData={userData} />}
                  />
                  <Route
                    path="companies/:companyId"
                    element={
                      <ReservedIdRouteGuard entity="company">
                        <CompanyDetailedView userData={userData} />
                      </ReservedIdRouteGuard>
                    }
                  />
                  <Route
                    path="companies/:companyId/log"
                    element={
                      <ReservedIdRouteGuard entity="company">
                        <History />
                      </ReservedIdRouteGuard>
                    }
                  />
                  <Route
                    path="companies/:companyId/pastContracts"
                    element={
                      <ReservedIdRouteGuard entity="company">
                        <PastContracts />
                      </ReservedIdRouteGuard>
                    }
                  />
                  <Route
                    path="people"
                    element={<People userData={userData} />}
                  />
                  <Route
                    path="people/:personId"
                    element={
                      <ReservedIdRouteGuard entity="person">
                        <PersonDetailedView userData={userData} />
                      </ReservedIdRouteGuard>
                    }
                  />
                  <Route
                    path="people/:personId/log"
                    element={
                      <ReservedIdRouteGuard entity="person">
                        <History />
                      </ReservedIdRouteGuard>
                    }
                  />
                  <Route path="leads" element={<Leads userData={userData} />} />
                  <Route path="leads/:leadId" element={<LeadDetailedView />} />
                  <Route path="leads/:leadId/log" element={<History />} />
                  <Route path="deals/:dealId" element={<DealDetailedView />} />
                  <Route path="deals" element={<Deals userData={userData} />} />
                  <Route path="deals/:dealId/log" element={<History />} />
                  {showCpq || cpqLoading ? (
                    <>
                      <Route
                        path="deals/:dealId/proposals/:proposalId"
                        element={
                          cpqLoading ? cpqSpinner : <ProposalEditorPage />
                        }
                      />
                      <Route
                        path="deals/:dealId/proposals/:proposalId/versions/:versionId"
                        element={
                          cpqLoading ? cpqSpinner : <ProposalVersionPage />
                        }
                      />
                    </>
                  ) : null}
                  <Route path="tasks/:taskId" element={<TaskPage />} />
                  <Route
                    path="contracts"
                    element={<Contracts userData={userData} />}
                  />
                  <Route
                    path="contracts/:contractId"
                    element={<ContractDetailedView />}
                  />
                  <Route
                    path="contracts/:contractId/log"
                    element={<History />}
                  />
                  {showDuplicateContract ? (
                    <Route
                      path="contracts/:contractId/duplicate"
                      element={<DuplicateContractPage />}
                    />
                  ) : null}
                  {showContractAmendments ? (
                    <Route
                      path="contracts/:contractId/terms/update"
                      element={<UpdateTermsPage />}
                    />
                  ) : null}
                  {showContractAmendments ? (
                    <Route
                      path="contracts/:contractId/terms/versions/:versionId"
                      element={<VersionTermsPage />}
                    />
                  ) : null}
                  <Route
                    path="services"
                    element={<Services userData={userData} />}
                  />
                  <Route
                    path="integrations"
                    element={
                      <Integrations userData={userData} account={accountData} />
                    }
                  />
                  <Route path="upsells" element={<Upsells />} />
                  {showPartnerships &&
                  showPartnersTable &&
                  enablePartnerships ? (
                    <Route
                      path="partners"
                      element={<Partners userData={userData} />}
                    />
                  ) : null}
                  {showPartnerships &&
                  showPartnersTable &&
                  enablePartnerships ? (
                    <Route
                      path="partners/:partnerId"
                      element={<PartnerDetailedView userData={userData} />}
                    />
                  ) : null}
                  {showPartnerships &&
                  showPartnersTable &&
                  enablePartnerships ? (
                    <Route
                      path="partners/:partnerId/log"
                      element={<History />}
                    />
                  ) : null}
                  {showPartnerships &&
                  showPartnerContacts &&
                  enablePartnerships ? (
                    <Route
                      path="partners_contacts/:partnerContactId"
                      element={
                        <PartnerContactDetailedView userData={userData} />
                      }
                    />
                  ) : null}
                  {showPartnerships &&
                  showPartnerContacts &&
                  enablePartnerships ? (
                    <Route
                      path="partners_contacts/:partnerContactId/log"
                      element={<History />}
                    />
                  ) : null}
                  <Route
                    path="downloads/:attachmentId"
                    element={<DownloadPage />}
                  />
                </Routes>
                {authRedirectProps && (
                  <AuthRedirect
                    {...authRedirectProps}
                    onDone={handleAuthRedirectDone}
                  />
                )}
              </main>
            </div>
          </div>
        </>
      )}
      {isSearchOpen && (
        <div
          className="fixed z-50 flex items-center justify-center"
          style={{
            top: `${bannerHeight + 8}px`,
            left: "50%",
            transform: "translateX(-50%)"
          }}
        >
          <GlobalSearch isOpen={isSearchOpen} onClose={closeSearch} />
        </div>
      )}

      <Toaster />
    </>
  );
};

export default MainNavigationAndAuth;
